import React from 'react';
import { motion } from 'framer-motion';
import img1 from "../../assets/about/img1.jpg";
import img2 from "../../assets/about/img2.jpg";
import img3 from "../../assets/about/canimg3.jpg";
import img4 from "../../assets/about/canimg4.jpg";

const FeatureSection = ({ title, description, image1, image2, reverse = false, link }) => {
  return (
    <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-24 items-center py-12 lg:py-20`}>
      {/* Images Section - stacked on mobile, overlapping on desktop */}
      <div className="w-full lg:w-1/2 relative">
        <motion.div
          initial={{ opacity: 0, x: reverse ? 50 : -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative z-10 w-full lg:w-[85%]"
        >
          <img src={image1} alt={title} className="w-full h-64 sm:h-80 lg:h-[500px] object-cover rounded-lg shadow-2xl" />
        </motion.div>
        {/* <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className={`hidden lg:block absolute ${reverse ? 'lg:-left-16' : 'lg:-right-16'} lg:-bottom-16 w-[65%] z-20`}
        >
          <div className="p-2 bg-linen rounded-sm shadow-xl">
            <img src={image2} alt={title} className="w-full h-[280px] object-cover rounded-sm" />
          </div>
        </motion.div> */}
        {/* Mobile: second image shown in a row below */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="block lg:hidden mt-3 w-[80%] mx-auto"
        >
          <div className="p-1.5 bg-white rounded-sm shadow-md">
            <img src={image2} alt={title} className="w-full h-48 object-cover rounded-sm" />
          </div>
        </motion.div>
      </div>

      {/* Text Section */}
      <div className="w-full lg:w-1/2 space-y-5 lg:space-y-6 mt-4 lg:mt-0 text-center lg:text-left">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl md:text-5xl font-serif text-charcoal leading-tight"
        >
          {title}
        </motion.h2>
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          className="h-[1px] w-20 bg-brass mx-auto lg:mx-0 origin-left"
        />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="space-y-4 text-charcoal/70 font-sans leading-relaxed text-base lg:text-lg"
        >
          {description}
        </motion.div>

        {link && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="pt-4"
          >
            <a 
              href={link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 border border-brass/30 text-brass bg-brass text-linen transition-all duration-500 rounded-sm text-[10px] font-bold tracking-[0.25em] uppercase group"
            >
              Read More
              <span className="ml-3 transform group-hover:translate-x-2 transition-transform duration-300">→</span>
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const Craftsmanship = () => {
  return (
    <section className="bg-linen py-24 select-none overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-24">
          <motion.span 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-brass uppercase tracking-widest text-sm font-semibold mb-4 block"
          >
            The Heritage
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-serif text-charcoal mb-6"
          >
            Our Craftsmanship
          </motion.h2>
          <p className="max-w-2xl mx-auto text-charcoal/60 font-sans text-lg">
            Preserving legacy through patience, precision, and passion. Each piece is a testament to the artisan's dedication.
          </p>
        </div>

        <FeatureSection 
          title="Master Carpentry"
          image1={img1}
          image2={img2}
          link="https://weavinghandblog.blogspot.com/2026/01/traditional-carpentry-work-in-furniture.html"
          description={
            <>
              <p>
                Our carpenters hail from the culturally rich regions of Tamil Nadu like Karaikudi and Chennai. They carry forward generations of woodworking knowledge.
              </p>
              <p>
                Our carpentry is not just about cutting and shaping wood; it's about preserving a legacy. Whether it’s an authentic Antique reproduction or a personalized piece, our work is built to endure lifetimes.
              </p>
            </>
          }
        />

        <div className="hidden lg:block pb-32" />

        <FeatureSection 
          title="Authentic Cane Weaving"
          image1={img3}
          // image2={img4}
          reverse={true}
          link="https://weavinghandblog.blogspot.com/2026/01/cane-weaving-in-handcrafted-furniture.html"
          description={
            <>
              <p>
                We take immense pride in authentic, handcrafted cane weaving. We absolutely never use machine-made or pre-woven cane in our heritage pieces.
              </p>
              <p>
                Our skilled weavers are masters of intricate, traditional patterns. Passed down through families, these techniques breathe life and unique character into every chair and cabinet door.
              </p>
            </>
          }
        />
      </div>
    </section>
  );
};

export default Craftsmanship;
